import { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import type { Zone } from '@/lib/api/zones'
import { startSession } from '@/lib/api/sessions'

const PLATE_RE = /^(?:\d{2}[A-Z]\d{3}[A-Z]{2}|\d{2}\d{3}[A-Z]{3})$/
const plateSchema = z.string().regex(PLATE_RE, 'plate_invalid')

const PRESETS = [30, 60, 120, 240] as const

interface Props {
  zone: Zone
  onClose: () => void
}

function formatUzs(tiyin: number): string {
  return new Intl.NumberFormat('uz-UZ').format(Math.round(tiyin / 100)) + ' UZS'
}

export function StartParkingDialog({ zone, onClose }: Props) {
  const { t } = useTranslation()
  const [plate, setPlate] = useState('')
  const [minutes, setMinutes] = useState<number>(60)
  const [plateError, setPlateError] = useState<string | null>(null)

  const tariff = zone.tariff
  const amountTiyin = useMemo(
    () => (tariff ? tariff.amountPerMin * minutes : 0),
    [tariff, minutes],
  )

  const mutation = useMutation({
    mutationFn: () =>
      startSession({ plate: plate.toUpperCase(), zoneId: zone.id, minutes }),
    onSuccess: (res) => {
      if (res.paymentUrl) {
        window.location.href = res.paymentUrl
      } else {
        alert(t('start.dev_no_payment', { sessionId: res.sessionId }))
        onClose()
      }
    },
  })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const parsed = plateSchema.safeParse(plate.toUpperCase())
    if (!parsed.success) {
      setPlateError(t('start.plate_invalid'))
      return
    }
    setPlateError(null)
    mutation.mutate()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">{zone.name}</h2>
            <p className="text-sm text-slate-500">
              {zone.code} · {zone.city}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {!tariff && (
          <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
            {t('start.no_tariff')}
          </p>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">{t('start.plate')}</span>
            <input
              value={plate}
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
              placeholder="01A123BC"
              maxLength={8}
              autoCapitalize="characters"
              spellCheck={false}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono uppercase tracking-wider focus:border-sky-500 focus:outline-none"
            />
            {plateError && <p className="mt-1 text-xs text-rose-600">{plateError}</p>}
          </label>

          <div>
            <span className="mb-1 block text-sm font-medium">{t('start.duration')}</span>
            <div className="grid grid-cols-4 gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setMinutes(p)}
                  className={
                    'rounded-lg border px-2 py-2 text-sm font-medium transition ' +
                    (minutes === p
                      ? 'border-sky-600 bg-sky-50 text-sky-700'
                      : 'border-slate-300 text-slate-700 hover:border-slate-400')
                  }
                >
                  {p < 60 ? `${p}m` : `${p / 60}h`}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
            <span className="text-sm text-slate-600">{t('start.total')}</span>
            <span className="text-lg font-semibold text-slate-900">
              {tariff ? formatUzs(amountTiyin) : '—'}
            </span>
          </div>

          {mutation.isError && (
            <p className="text-sm text-rose-600">
              {(mutation.error as { message?: string })?.message ?? t('start.error')}
            </p>
          )}

          <button
            type="submit"
            disabled={!tariff || mutation.isPending}
            className="w-full rounded-lg bg-sky-600 py-3 font-medium text-white hover:bg-sky-700 disabled:opacity-50"
          >
            {mutation.isPending ? t('start.submitting') : t('start.pay_and_park')}
          </button>
        </form>
      </div>
    </div>
  )
}
